import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import { Grid, Paper, Container, Theme, Stack, Typography, LinearProgress, linearProgressClasses, Box, Divider } from '@mui/material'
import { getSelectedMultipleChoiceQuizInitiated } from '../../../store/dashboard/dashboard.actions'
import { getQuizData } from '../../../store/dashboard/dashboard.selector'
import UserQuizQuestion from './UserQuizQuestion'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { getUser } from '../../../store/common/common.selectors'
import { UpdateUserAccountRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import { updateUserAccountInitiated } from '../../../store/common/common.actions'
import useQuery from '../../../util/useQuery'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginTop: theme.spacing(3),
    padding: theme.spacing(5),
  },
  countdown: {
    display: 'flex',
    position: 'absolute',
    justifyContent: 'center',
    width: '100%',
    paddingTop: theme.spacing(2),
    top: 0,
  },
}))

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 19,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}))

const UserQuiz: React.FC<{setQuizCompleted: () => void}> = ({ setQuizCompleted }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const query = useQuery()

  const user = useSelector(getUser)
  const quizData = useSelector(getQuizData)

  const [activeStep, setActiveStep] = React.useState(0)
  const [completed, setCompleted] = React.useState(false)
  const [currentAnswers, setCurrentAnswers] = React.useState<{
    [k: number]: boolean
  }>({})

  const dispatchGetSelectedMultipleChoiceQuiz = useCallback(() => dispatch(getSelectedMultipleChoiceQuizInitiated()), [dispatch])
  const dispatchUpdateUserAccount = useCallback((params: UpdateUserAccountRequest) => dispatch(updateUserAccountInitiated(params)), [dispatch])

  const ratio = quizData?.questions && activeStep !== 0 ? activeStep * 100 / quizData.questions.length : 0

  const hasAllCorrect = () => {
    if (!(quizData && quizData.questions && Object.keys(currentAnswers).length !== 0 && Object.values(currentAnswers).every(item => item))) return false
    const correctAnswers = quizData.questions[activeStep].answers?.filter(answer => answer.correct)

    // compare correct answers with current answers keys, to make sure all correct answers are chosen
    return correctAnswers?.every(answer => Object.keys(currentAnswers).includes(answer.id.toString()))
  }

  const allStepsCompleted = () => {
    if (!quizData?.questions) return false
    return activeStep === quizData.questions.length - 1
  }

  const handleSetCurrentAnswers = (index: number, value?: boolean | undefined) => {
    value !== undefined
      ? setCurrentAnswers({ [index]: value, ...currentAnswers })
      : setCurrentAnswers(current => {
        const obj = { ...current }
        delete obj[index]
        return obj
      })
  }

  const handleNext = () => {
    if (allStepsCompleted()) {
      setCompleted(true)
      user?.id && dispatchUpdateUserAccount({ userId: user.id, userLogin: { ...user, finishedQuiz: true } })
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
      setCurrentAnswers({})
    }
  }

  useEffect(() => {
    if (query.get('login_as')) {
      history.push('/')
    }
  })

  useEffect(() => {
    dispatchGetSelectedMultipleChoiceQuiz()
  }, [])

  return (
    <Container maxWidth='lg'>
      <Grid container sx={{ px: 2, pb: 12 }}>
        <Paper className={classes.paper}>
          <Grid container item spacing={2} xs={12}>
            <Grid item xs={12}>
              {quizData && quizData.questions && (
                <>
                  {completed ? (
                    <Stack spacing={3} alignItems='center' width='100%'>
                      <Typography variant='h2'>{t('coreWizard:congratulation')}</Typography>
                      <Typography align='center'>{t('coreWizard:quizEnding')}</Typography>
                      <ButtonComponent
                        text={t('coreWizard:toDashboard')}
                        onClick={() => {
                          setQuizCompleted()
                          location.reload()
                        }}
                      />
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Typography variant='h2'>{quizData.title}</Typography>
                      <Typography>{quizData.description}</Typography>
                      <Divider sx={{ my: 4 }} />
                      <Box sx={{ position: 'relative', p: 2 }}>
                        <Box sx={{ width: '100%' }}>
                          <BorderLinearProgress variant='determinate' value={ratio} />
                        </Box>
                        <Box className={classes.countdown}>
                          <Typography variant='body2' color={Math.round(ratio) <= 50 ? 'text.secondary' : 'primary.contrastText'} sx={{ fontWeight: 'bold' }}>
                            {`${Math.round(ratio)}%`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ px: 2, pt: 2 }}>
                        <UserQuizQuestion
                          question={quizData.questions[activeStep]}
                          setCurrentAnswers={handleSetCurrentAnswers}
                        />
                      </Box>
                      <Divider sx={{ my: 4 }} />
                      <Stack direction='row' justifyContent='flex-end'>
                        <Box>
                          <ButtonComponent
                            variant='contained'
                            color='primary'
                            disabled={!hasAllCorrect()}
                            onClick={handleNext}
                            text={allStepsCompleted() ? t('coreWizard:finish') : t('coreWizard:continue')}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  )}
                </>

              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Container>
  )
}

export default UserQuiz
